import React, { type JSX, useState } from 'react';
import { Button } from 'antd';
import SystemTypeRender from '../SystemTypeRender';
import { useRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import { useSystemType } from 'vl-common/src/context/provider.app';
import styled from '@emotion/styled';
import logRocketLogo from '../../../assets/img/logRocket.png';
import hbsukLogo from '../../../assets/img/hbsuk_logo.png';
import UIVersionLabel from '../UIVersionLabel';
import { HarFunctionsType, useHARFunctions } from 'vl-common/src/hooks/useHARFunctions';
import TestingModal from './TestingModal';
import DumpModal from './DumpModal';

const StyledFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background-color: #fff;
  border-top: solid 1px var(--productIron);
  padding: 5px 14px;
  font-size: 14px;

  position: fixed;
  bottom: 0;
  width: 100%;

  &.extreme-zoom {
    zoom: 0.5;
  }
`;

export const CoreFooter: React.FC<{
  logRocketSessionURL?: string;
  ThemeButton?: () => JSX.Element;
  AccessibilityContainer?: () => JSX.Element;
  footerClass?: string;
  harProps: HarFunctionsType;
}> = ({
  logRocketSessionURL,
  ThemeButton = () => <></>,
  AccessibilityContainer = () => <></>,
  footerClass = '',
  harProps
}) => {
  const { LUCY_ENV, DATABASE_VERSION } = useRuntimeConfig();
  const [type] = useSystemType();
  const isProd = LUCY_ENV.includes('prod');

  const [devToolsIsVisible, setDevToolsIsVisible] = useState(true);

  const {
    clearButtonProps,
    dumpButtonProps,
    recordButtonProps,
    testingButtonProps,
    disableButtonProps,
    harIsRunning,
    enterTestingMode,
    exitTestingMode,
    inTestingMode,
    setInTestingMode,
    testingModalOpen,
    setTestingModalOpen,
    dumpModalOpen,
    setDumpModalOpen,
    dumpedHAR
  } = harProps;

  const systemTypeLabels = {
    admin: '(HBS) client admin',
    referrer: '(client) referrer',
    'referrer-admin': '(client) referrer admin',
    clinician: 'clinician',
    patient: 'patient'
  };
  const systemTypeLabel = systemTypeLabels[type as keyof typeof systemTypeLabels];

  return (
    <StyledFooter className={footerClass}>
      <span>
        <UIVersionLabel />
      </span>
      <img
        src={
          //  a (hopefully) temporary measure to accomodate Next’s Webpack image loader
          typeof hbsukLogo === 'string' ? hbsukLogo : hbsukLogo.src
        }
        alt="HBS logo"
        width="40px"
      />{' '}
      <SystemTypeRender APP>
        <a
          rel="noreferrer"
          target="_blank"
          href="/patients/legal/?all_policies=0&activeTab=USAGE"
          tabIndex={0}
          data-testid="terms"
        >
          Terms
        </a>
        <a rel="noreferrer" target="_blank" href="/accessibility" tabIndex={0} data-testid="accessibility">
          Accessibility
        </a>
        <a rel="noreferrer" target="_blank" href="/regulatory" tabIndex={0} data-testid="regulatory">
          <img src="/static/images/ukca.png" alt="UKCA Mark" width="25px" />
        </a>
      </SystemTypeRender>
      <SystemTypeRender ADMIN>
        {!isProd && (
          <span>
            &nbsp;Type: {systemTypeLabel}, &quot;{type}&quot;
          </span>
        )}
      </SystemTypeRender>
      {!isProd && devToolsIsVisible && (
        <>
          Dev Tools:{' '}
          {logRocketSessionURL && (
            <a href={logRocketSessionURL} target="_blank" data-testid="logrocket" rel="noreferrer">
              <img
                src={
                  // a (hopefully) temporary measure to accomodate Next’s Webpack image loader
                  typeof logRocketLogo === 'string' ? logRocketLogo : logRocketLogo.src
                }
                width="30px"
                alt="LogRocket"
              />
            </a>
          )}
          <ThemeButton data-testid="theme-toggle" />
          <div data-testid="db-version">DB Version: {DATABASE_VERSION}</div>
          <Button data-testid="hide-dev-footer" onClick={() => setDevToolsIsVisible(false)} size="small">
            Hide
          </Button>
          <Button {...clearButtonProps.props} data-testid="footer-clear-button">
            {clearButtonProps.text}
          </Button>
          <Button {...recordButtonProps.props} data-testid="footer-recrod-button">
            {recordButtonProps.text}
          </Button>
          <Button {...dumpButtonProps.props} data-testid="footer-dumo-button">
            {dumpButtonProps.text}
          </Button>
          <Button {...testingButtonProps.props} data-testid="footer-testing-button">
            {testingButtonProps.text}
          </Button>
          {harIsRunning && (
            <Button {...disableButtonProps.props} data-testid="footer-har-button">
              {disableButtonProps.text}
            </Button>
          )}
        </>
      )}
      {!isProd && !devToolsIsVisible && (
        <Button
          onClick={() => setDevToolsIsVisible(true)}
          className="dev-tools-item"
          size="small"
          data-testid="show-dev-tools"
        >
          Show dev tools
        </Button>
      )}
      <AccessibilityContainer />
      <TestingModal
        visible={testingModalOpen}
        onCancel={() => {
          setTestingModalOpen(false);
        }}
        enterTestingMode={enterTestingMode}
        exitTestingMode={exitTestingMode}
        inTestingMode={inTestingMode}
        setInTestingMode={setInTestingMode}
      />
      <DumpModal
        visible={dumpModalOpen}
        har={dumpedHAR || undefined}
        onCancel={() => {
          setDumpModalOpen(false);
        }}
      />
    </StyledFooter>
  );
};

const Footer: React.FC<{
  className?: string;
  logRocketSessionURL?: string;
  getDynamicConfig: any;
  ThemeButton?: () => JSX.Element;
  footerClass?: string;
}> = ({ className, logRocketSessionURL, getDynamicConfig, ThemeButton = () => <></>, footerClass }) => {
  const harProps = useHARFunctions(getDynamicConfig);

  return (
    <footer {...{ className }}>
      <CoreFooter
        logRocketSessionURL={logRocketSessionURL}
        ThemeButton={ThemeButton}
        footerClass={footerClass}
        harProps={harProps}
      />
    </footer>
  );
};

export default Footer;
